/**
 * ``usePausedListRows`` — what the four operator list panels render while
 * their **Follow live updates** toggle is released (REQ-UI-182 amended
 * 2026-10-10; operator report 2026-10-10).
 *
 * # Why a freeze, and not a scroll correction
 *
 * Until 2026-10-10 "not following" meant *compensating*: rows kept arriving
 * and :func:`@/selection/use-keep-selected-row-in-view` scrolled the list on
 * every update to hold the operator's first visible row at its viewport
 * offset. That is only stable while the list is an append-above of a buffer
 * with room to grow, and neither holds under load:
 *
 *   - **Detections is a 500-row ring** (``DETECTION_BUFFER_CAP``). At 60
 *     tracks × 3 Hz the whole buffer turns over in under three seconds, so
 *     the anchor row is pruned almost immediately, ``scrollTop`` is driven to
 *     its maximum and the viewport then sits pinned at the bottom of the ring
 *     while content streams through it — measured on the hermetic rig
 *     2026-10-10: ``scrollTop`` pinned at 45909 for 30 s with the first
 *     visible row changing on 74 of 80 samples. Indistinguishable from the
 *     auto-scrolling the operator had just switched off.
 *   - **Tracks re-sorts.** Holding a row's *offset* means the scrollbar
 *     travels down as rows insert above (2227 → 5502 px in 30 s on the rig),
 *     jittering by a row height on nearly every update, and any re-sort
 *     teleports the viewport — including back to the top when the held row is
 *     re-sorted near it, which is the scrollbar that "jumps down and resets".
 *
 * So the released toggle now **pauses the list**: the rows on screen are the
 * rows that were there at the flip. Nothing is inserted, nothing is pruned,
 * nothing is re-sorted, and therefore nothing moves — the hold needs no
 * scrolling at all. Resuming returns to the live rows and pins to the top
 * (the keep-in-view hook's follow-flip path).
 *
 * # What stays live while paused
 *
 *   - **Each held row's own record.** The frozen thing is the row *set and
 *     order*, not the values: a held track's head time and detection count
 *     keep updating, and a platform's health state keeps updating, because a
 *     stale status on the Platforms list is exactly the kind of hidden state
 *     CLAUDE.md §8.1 rules out. Row geometry does not change, so the list
 *     still does not move.
 *   - **A row the live buffer has dropped** keeps rendering from the frozen
 *     snapshot. The operator paused the list to read those rows; losing them
 *     to a ring-buffer eviction would defeat the pause.
 *   - **Everything outside this panel.** The stream, the map, the other
 *     panels and Entity Details are untouched — this is a rendering pause for
 *     one list, not the SSE connection's ``paused`` state in
 *     :mod:`@/map/use-sse`.
 *   - **A selected row that is not held** is *admitted* to the frozen view in
 *     its live sort position, so the map→list reveal (REQ-UI-051) still has a
 *     row to reveal while the list is paused. Once admitted it stays, because
 *     taking it away again on deselect would move the list under the
 *     operator.
 *
 * The count of rows withheld is returned so the panel header can say so
 * (operator visibility, CLAUDE.md §8.1): a paused list that quietly hides
 * 1,200 new detections is worse than no pause at all.
 */
import { useMemo, useState } from "react";

/** What a paused (or following) list should render, and what it is hiding. */
export interface PausedListRows<T> {
  /** The rows to render: the live rows while following, else the frozen set. */
  readonly rows: readonly T[];
  /** How many live rows the frozen set is not showing (0 while following). */
  readonly withheld: number;
}

/** Per-call inputs. */
export interface PausedListRowsOptions<T> {
  /** The panel's live, sorted rows. */
  readonly rows: readonly T[];
  /** The panel's follow flag (:func:`useListFollow`). */
  readonly follow: boolean;
  /** A row's stable identity (``report_id`` / ``track_id`` / …). */
  readonly idOf: (row: T) => string;
  /**
   * Ids that must be present even while paused — this panel's inspected and
   * map-selected ids. ``null`` entries are ignored, as are ids the live rows
   * do not carry.
   */
  readonly admit?: readonly (string | null)[];
}

/** The frozen row set: the order on screen, and each row as captured. */
interface Snapshot<T> {
  readonly order: readonly string[];
  readonly byId: ReadonlyMap<string, T>;
}

/** Freeze the rows as they are now. */
function capture<T>(rows: readonly T[], idOf: (row: T) => string): Snapshot<T> {
  const byId = new Map<string, T>();
  for (const row of rows) {
    byId.set(idOf(row), row);
  }
  return { order: rows.map(idOf), byId };
}

/**
 * Add any ``admit`` id the snapshot lacks, positioned the way the live order
 * positions it (before the first held row that sorts after it), so an
 * admitted row lands where the operator would expect to find it rather than
 * at the top. Returns the same snapshot when there is nothing to admit, which
 * is what lets the caller write it back during render without looping.
 */
function admitInto<T>(
  snapshot: Snapshot<T>,
  rows: readonly T[],
  idOf: (row: T) => string,
  admit: readonly (string | null)[] | undefined,
): Snapshot<T> {
  if (admit === undefined) {
    return snapshot;
  }
  // De-duplicated: the two selection slots routinely hold the SAME id (a
  // "Select on Map" sets both), and admitting it once per slot rendered the
  // row twice.
  const wanted = [
    ...new Set(admit.filter((id): id is string => id !== null && !snapshot.byId.has(id))),
  ];
  if (wanted.length === 0) {
    return snapshot;
  }
  const livePosition = new Map<string, number>();
  rows.forEach((row, index) => {
    livePosition.set(idOf(row), index);
  });
  const order = [...snapshot.order];
  const byId = new Map(snapshot.byId);
  let added = false;
  for (const id of wanted) {
    const position = livePosition.get(id);
    if (position === undefined) {
      continue; // Selected, but this panel's live rows do not carry it.
    }
    const record = rows[position];
    const at = order.findIndex((held) => (livePosition.get(held) ?? Infinity) > position);
    order.splice(at === -1 ? order.length : at, 0, id);
    byId.set(id, record);
    added = true;
  }
  return added ? { order, byId } : snapshot;
}

/**
 * The rows a list panel should render for its follow flag.
 *
 * @param options - see :type:`PausedListRowsOptions`.
 * @returns the rows to render and the withheld count.
 */
export function usePausedListRows<T>({
  rows,
  follow,
  idOf,
  admit,
}: PausedListRowsOptions<T>): PausedListRows<T> {
  const [snapshot, setSnapshot] = useState<Snapshot<T> | null>(null);
  // Render-time state adjustment (React's "adjust state when a prop changes"
  // pattern, as the Tracks panel's selection-driven expansion uses): the
  // frozen set has to land in the SAME commit as the flip, or the list
  // renders one frame of live rows — a visible jump at the moment the
  // operator asked it to stop moving. ``effective`` is what this render uses,
  // so the adjusting pass renders the right thing too.
  let effective = snapshot;
  if (follow) {
    effective = null;
    if (snapshot !== null) {
      setSnapshot(null);
    }
  } else if (snapshot === null) {
    effective = capture(rows, idOf);
    setSnapshot(effective);
  } else {
    effective = admitInto(snapshot, rows, idOf, admit);
    if (effective !== snapshot) {
      setSnapshot(effective);
    }
  }

  return useMemo((): PausedListRows<T> => {
    if (effective === null) {
      return { rows, withheld: 0 };
    }
    const live = new Map<string, T>();
    for (const row of rows) {
      live.set(idOf(row), row);
    }
    const held: T[] = [];
    for (const id of effective.order) {
      // The live record when the buffer still has it (so the row's own fields
      // stay current), else the copy frozen at the flip.
      const record = live.get(id) ?? effective.byId.get(id);
      if (record !== undefined) {
        held.push(record);
      }
    }
    let withheld = 0;
    for (const id of live.keys()) {
      if (!effective.byId.has(id)) {
        withheld += 1;
      }
    }
    return { rows: held, withheld };
  }, [rows, effective, idOf]);
}
